import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ItemNames } from '../ItemNames';
import { ChangeRequestsPanel, EditOrder } from './EditOrder';
import { backToOrders, invoiceLinkState, ORDERS_HOME } from './back-target';
/* D75 — the ONE due-rule formatter. "Terms on this order" states the rule, never builds it. */
import { dueRuleWords } from '../payables-view';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { api, ApiError } from '../../api/client';
import { budgetExceeded, kycRequired } from '../../api/errors';
import { KycRefusal } from './KycRefusal';
import {
  useAcceptOrder, useCart, useClearCart, useDeclineOrder, useLastOrder, useMyOrder, useMyOrders,
  useOrderPayments, useRequestRevision, useShopOrderConsignmentInvoices, useSendMyOrderMessage, useSetCartLine, useSubmitOrder, useSubmitPaymentProof,
  uploadPaymentProofFile, useShopOrderMilestones,
} from '../../api/hooks';
import {
  REVISION_FEEDBACK_SUBJECT,
  type PaymentProof, type RetailerOrderSummary, type PaymentProofStatus, type ShopAccount, type ShopVariant,
  type ShopVariantDetail,
} from '../../api/types';
import { useSession } from '../../auth/session';
import {
  parsePaste, emptyGridRow, gridRowError, gridRowIsValid, type GridRow,
} from './order-lines';
import {
  Alert, Button, Chip, Empty, Field, Money, Note, PageHeader, Panel, Rail, Spinner, Table, Tabs, type Tone,
} from '../../ui';
import { OrderChat } from '../../ui/OrderChat';
import { addMoney, mulMoney } from './line-money';
import { ThumbPreview } from './ThumbPreview';
import { SHIPMENT_TONE } from './consignment-invoice-tone';
import { shipmentStateLabel } from '../shipment-state';
import { RetailerMilestones } from './order/RetailerMilestones';
import { noticeFor } from '../order/amount-notice';
import { OrderActions } from './OrderActions';
import { shipmentLine } from './order-list-view';
import { filterOrders, filtersFromParams, isFiltering, NO_FILTERS, paramsFromFilters, type OrderFilters } from './order-filters';
import { ShipmentStatus } from './ShipmentStatus';

/**
 * R-04 (bulk entry) and R-05 (cart review), ported from `retailer-ordering.html`'s
 * "doors" interaction model — minus the "browse" door, which is R-02
 * (`screens/Shop.tsx`'s `ShopCatalog`) and not duplicated here.
 *
 * Cart state comes from `GET /shop/cart` / `PUT /shop/cart/lines`, never from
 * local component state — that is the whole point of this screen surviving a
 * refresh. `useCart`/`useSetCartLine` live in `api/hooks.ts` so this file and
 * `Shop.tsx`'s add-to-cart control share one mutation instead of duplicating it.
 */

const dash = '—';

// ---------------------------------------------------------------- money, display-only
//
// `@miraki/money` does not reach the browser — see the note on `useSetCartLine`
// in `api/hooks.ts`, confirmed this session by requesting the package's dist
// file through the running Vite dev server: it is CommonJS, absent from
// `node_modules/.vite/deps/_metadata.json` (not pre-bundled), and a browser's
// native ESM loader cannot resolve named imports from a file with no `export`
// statement. Everything below is exact decimal-string arithmetic via BigInt at
// NUMERIC(19,4)'s scale, in the spirit of NF-04 (never a JS float for a money
// *value*) — and it is display-only: a cart line never carries a price
// (invariant 6), so none of this is ever sent back to the API. The one
// The credit meter it mentioned is gone with D37, and `fmtMoney` went with it — it existed
// solely to render the over-limit alert body as a plain string.

// -------------------------------------------------------------------------- qty stepper

/**
 * One row updated by committing on blur/Enter/stepper-click, not on every
 * keystroke — `retailer-ordering.html:306-308`'s comment on why (redrawing from
 * a change handler throws focus away mid-order) applies just as much to a
 * server round-trip as it did to the prototype's in-memory redraw.
 */
function QtyStepper(
  { value, onCommit, disabled }: { value: number; onCommit: (n: number) => void; disabled?: boolean },
) {
  const { t } = useTranslation();
  const [text, setText] = useState(value ? String(value) : '');
  useEffect(() => { setText(value ? String(value) : ''); }, [value]);

  const commit = (raw: string) => {
    const n = Math.max(0, Math.round(Number(raw.replace(/[^\d]/g, '')) || 0));
    setText(n ? String(n) : '');
    if (n !== value) onCommit(n);
  };

  return (
    <span className="mk-qty">
      <button
        type="button"
        disabled={disabled || value <= 0}
        onClick={() => commit(String(Math.max(0, value - 1)))}
        aria-label={t('shop.addToCart.fewer')}
      >−</button>
      <input
        inputMode="numeric"
        value={text}
        placeholder="0"
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'ArrowUp') { e.preventDefault(); commit(String(value + 1)); }
          if (e.key === 'ArrowDown') { e.preventDefault(); commit(String(Math.max(0, value - 1))); }
        }}
      />
      <button type="button" disabled={disabled} onClick={() => commit(String(value + 1))} aria-label={t('shop.addToCart.more')}>+</button>
    </span>
  );
}

type Door = 'grid' | 'find' | 'again' | 'paste';

/** R-04 — bulk order entry: grid (the default), find, order again, paste.
 *  Plus the pinned bar. */
export function ShopOrder() {
  const { t } = useTranslation();
  const { me } = useSession();
  const navigate = useNavigate();
  const [door, setDoor] = useState<Door>('grid');
  const [query, setQuery] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pasteNote, setPasteNote] = useState<{ added: number; missed: string[] } | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [applyingAgain, setApplyingAgain] = useState(false);
  const [gridRows, setGridRows] = useState<GridRow[]>(
    () => Array.from({ length: 4 }, () => emptyGridRow(crypto.randomUUID())),
  );

  const canOrder = me?.kind === 'retailer' && me.permissions.includes('shop.order');
  const currency = me?.kind === 'retailer' ? me.retailer.tradingCurrency : undefined;

  const { data: catalog, isLoading: catalogLoading, error: catalogError } = useQuery({
    queryKey: ['shop', 'catalog'],
    queryFn: () => api.get<ShopVariant[]>('/shop/catalog'),
  });
  const { data: cart, isLoading: cartLoading, error: cartError } = useCart();
  const { data: lastOrder } = useLastOrder();

  const setLine = useSetCartLine();
  const clearCart = useClearCart();

  const cartByCode = useMemo(
    () => new Map((cart?.lines ?? []).map((l) => [l.variantCode, l])),
    [cart],
  );
  const lastCodes = useMemo(
    () => new Set((lastOrder?.lines ?? []).map((l) => l.variantCode)),
    [lastOrder],
  );

  /** Sequential on purpose: one cart, upserted by variant code — parallel PUTs
   *  would race each other's "return the recomputed cart" response into the
   *  query cache, and there is no bulk endpoint (choke point, implementation
   *  spec §2 — `CartService.setLine` is the only write path). */
  const bulkApply = async (entries: Array<{ code: string; qty: number; size?: string | undefined }>) => {
    setAddError(null);
    for (const e of entries) {
      await setLine.mutateAsync({ variantCode: e.code, quantity: e.qty, size: e.size });
    }
  };

  /**
   * Every `bulkApply` caller goes through this.
   *
   * All three used to fire and forget: a rejected add — an unpublished
   * variant, a dropped connection — left the rows exactly as they were, showed
   * nothing, and navigated nowhere. The retailer's only available reading was
   * that the button does not work, so they press it again and add everything
   * twice. Whatever fails, it is now said out loud, and the success path only
   * runs when there was one.
   *
   * `entries` is reported alongside because a partial batch is the common
   * case: `bulkApply` is sequential, so the lines before the failure are in
   * the cart and the ones after it are not.
   */
  const runBulk = (
    entries: Array<{ code: string; qty: number; size?: string | undefined }>,
    onDone: () => void,
    onSettled?: () => void,
  ) => {
    void bulkApply(entries).then(
      () => { onDone(); },
      (err: unknown) => {
        setAddError(err instanceof Error ? err.message : t('shop.order.addFailed'));
      },
    ).finally(() => onSettled?.());
  };

  const onDoorClick = (next: Door) => {
    setDoor(next);
    if (next === 'again' && lastOrder && lastOrder.lines.length > 0) {
      setApplyingAgain(true);
      // CR-44 — carries the size ordered last time forward verbatim. If that
      // size is no longer one of the product's current sizeOptions, that only
      // surfaces later (e.g. on the review screen or a future edit); Order
      // again's job is to reproduce the last order, not to re-validate it.
      runBulk(
        lastOrder.lines.map((l) => ({ code: l.variantCode, qty: l.quantity, size: l.size ?? undefined })),
        () => {},
        () => setApplyingAgain(false),
      );
    }
  };

  // ------------------------------------------------------------------ grid door

  const updateGridRow = (id: string, patch: Partial<GridRow>) => {
    setGridRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addGridRow = () => setGridRows((rows) => [...rows, emptyGridRow(crypto.randomUUID())]);

  const removeGridRow = (id: string) => setGridRows((rows) => {
    const next = rows.filter((r) => r.id !== id);
    return next.length ? next : [emptyGridRow(crypto.randomUUID())];
  });

  /** Resolves a row's code against the live catalog on blur — mirrors
   *  `Shop.tsx`'s buy-panel lookup (`GET /shop/catalog/:code`), one row at a
   *  time rather than a batch endpoint (implementation spec §3). Guards
   *  against a stale response landing after the cell was edited again by
   *  re-checking the row's *current* code before applying the result. */
  const resolveGridRow = async (id: string, rawCode: string) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) {
      updateGridRow(id, { code: '', variant: null, sizeOptions: [], notFound: false, resolving: false });
      return;
    }
    updateGridRow(id, { code, resolving: true, notFound: false });
    try {
      const detail = await api.get<ShopVariantDetail>(`/shop/catalog/${encodeURIComponent(code)}`);
      setGridRows((rows) => rows.map((r) => (r.id === id && r.code === code
        ? {
          ...r,
          variant: detail,
          sizeOptions: detail.sizeOptions,
          resolving: false,
          notFound: false,
          size: detail.sizeOptions.includes(r.size) ? r.size : '',
        }
        : r)));
    } catch {
      setGridRows((rows) => rows.map((r) => (r.id === id && r.code === code
        ? { ...r, variant: null, sizeOptions: [], resolving: false, notFound: true }
        : r)));
    }
  };

  const gridValidRows = useMemo(() => gridRows.filter(gridRowIsValid), [gridRows]);
  const gridErrorCount = useMemo(
    () => gridRows.filter((r) => gridRowError(r) !== null).length,
    [gridRows],
  );
  const gridTotal = useMemo(
    () => gridValidRows.reduce(
      (sum, r) => addMoney(sum, mulMoney(r.variant!.listPrice, parseInt(r.qty, 10))),
      '0.0000',
    ),
    [gridValidRows],
  );
  const gridCanSubmit = canOrder && gridErrorCount === 0 && gridValidRows.length > 0;

  const submitGrid = () => {
    runBulk(
      gridValidRows.map((r) => ({
        code: r.variant!.code,
        qty: parseInt(r.qty, 10),
        size: r.sizeOptions.length > 0 ? r.size : undefined,
      })),
      () => {
        setGridRows(Array.from({ length: 4 }, () => emptyGridRow(crypto.randomUUID())));
        // react-router v7 returns a promise from navigate; nothing waits on it.
        void navigate('/shop/order/review');
      },
    );
  };

  const applyPaste = () => {
    if (!catalog) return;
    const { entries, missed } = parsePaste(pasteText, catalog);
    runBulk(entries, () => {
      setPasteNote({ added: entries.length, missed });
      setDoor('find');
      setQuery('');
      setPasteText('');
    });
  };

  const rows = useMemo(() => {
    let list: ShopVariant[] = catalog ?? [];
    if (door === 'again') list = list.filter((v) => lastCodes.has(v.code));
    if (door === 'find' && query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((v) => [v.code, v.product, v.purity, v.metalColour]
        .some((f) => (f ?? '').toLowerCase().includes(q)));
    }
    return list;
  }, [catalog, door, query, lastCodes]);

  if (catalogLoading || cartLoading) return <Spinner />;
  if (catalogError) return <Alert tone="stop">{catalogError.message}</Alert>;
  if (cartError) return <Alert tone="stop">{cartError.message}</Alert>;

  const lineCount = cart?.lines.length ?? 0;
  const pieces = (cart?.lines ?? []).reduce((sum, l) => sum + l.quantity, 0);
  const total = cart?.total ?? '0.0000';
  const showTable = door !== 'grid' && door !== 'paste'
    && !(door === 'again' && lastCodes.size === 0 && !applyingAgain);

  return (
    <>
      <PageHeader title={t('shop.order.title')} />
      <Tabs
        tabs={[
          { id: 'grid', label: t('shop.order.doorGrid') },
          { id: 'find', label: t('shop.order.doorFind') },
          { id: 'again', label: t('shop.order.doorAgain'), disabled: applyingAgain },
          { id: 'paste', label: t('shop.order.doorPaste') },
        ]}
        active={door}
        onChange={(id) => onDoorClick(id as Door)}
      />
      {door === 'grid' ? (
        <Panel
          title={t('shop.order.doorGrid')}
          actions={<Button tone="ghost" size="sm" onClick={addGridRow}>{t('shop.order.gridAddRow')}</Button>}
        >
          <p className="mk-hint" style={{ padding: '0 var(--mk-space-4)', margin: '12px 0' }}>
            {t('shop.order.gridSub')}
          </p>
          <Table<GridRow>
            rows={gridRows}
            rowKey={(r) => r.id}
            dense={false}
            columns={[
              {
                header: t('shop.order.gridColCode'),
                render: (r) => (
                  <>
                    <input
                      className="mk-fld"
                      style={{ textTransform: 'uppercase' }}
                      placeholder={t('shop.order.gridColCode')}
                      value={r.code}
                      disabled={!canOrder}
                      onChange={(e) => updateGridRow(r.id, { code: e.target.value })}
                      onBlur={(e) => void resolveGridRow(r.id, e.target.value)}
                    />
                    {gridRowError(r) === 'unrecognised' ? (
                      <span className="mk-row-error">{t('shop.order.gridUnrecognised')}</span>
                    ) : null}
                  </>
                ),
              },
              {
                header: t('shop.order.gridColProduct'),
                render: (r) => (r.variant ? (
                  <>
                    <div>{r.variant.product ?? dash}</div>
                    <div className="mk-dim">
                      {[r.variant.metalColour, r.variant.purity].filter(Boolean).join(' · ') || dash}
                    </div>
                  </>
                ) : <span className="mk-dim">{dash}</span>),
              },
              {
                header: t('shop.order.gridColQty'), numeric: true,
                render: (r) => (
                  <>
                    <input
                      inputMode="numeric"
                      className="mk-fld"
                      style={{ width: 62, textAlign: 'center' }}
                      value={r.qty}
                      disabled={!canOrder}
                      onChange={(e) => updateGridRow(r.id, { qty: e.target.value })}
                    />
                    {gridRowError(r) === 'quantity' ? (
                      <span className="mk-row-warn">{t('shop.order.gridQuantityError')}</span>
                    ) : null}
                  </>
                ),
              },
              {
                header: t('shop.order.gridColSize'),
                render: (r) => {
                  if (!r.variant) return <span className="mk-dim">{dash}</span>;
                  if (r.sizeOptions.length === 0) {
                    return <span className="mk-hint">{t('shop.order.gridNotSized')}</span>;
                  }
                  return (
                    <>
                      <select
                        className="mk-fld"
                        value={r.size}
                        disabled={!canOrder}
                        onChange={(e) => updateGridRow(r.id, { size: e.target.value })}
                      >
                        <option value="">{t('shop.order.gridSizePlaceholder')}</option>
                        {r.sizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {gridRowError(r) === 'size' ? (
                        <span className="mk-row-warn">{t('shop.order.gridSizeError')}</span>
                      ) : null}
                    </>
                  );
                },
              },
              {
                header: t('shop.order.gridColUnit'), numeric: true,
                render: (r) => (r.variant
                  ? <Money amount={r.variant.listPrice} currency={currency} />
                  : <span className="mk-dim">{dash}</span>),
              },
              {
                header: t('shop.order.gridColLineTotal'), numeric: true,
                render: (r) => (gridRowIsValid(r)
                  ? <Money amount={mulMoney(r.variant!.listPrice, parseInt(r.qty, 10))} currency={currency} />
                  : <span className="mk-dim">{dash}</span>),
              },
              {
                header: '',
                render: (r) => (
                  <button
                    type="button"
                    className="mk-btn mk-btn-g mk-btn-s"
                    onClick={() => removeGridRow(r.id)}
                    aria-label={t('common.remove')}
                  >×</button>
                ),
              },
            ]}
          />
          <div className="mk-grid-footer">
            <span>
              {t('shop.order.gridSummary', { count: gridValidRows.length })}
              {gridErrorCount > 0 ? ` · ${t('shop.order.gridSummaryErrors', { count: gridErrorCount })}` : ''}
            </span>
            <span className="mk-spacer" />
            {gridValidRows.length > 0 && gridErrorCount === 0 ? (
              <Chip tone="done">
                <Money amount={gridTotal} currency={currency} /> {t('shop.order.gridReadyLabel')}
              </Chip>
            ) : null}
            <Button onClick={submitGrid} disabled={!gridCanSubmit || setLine.isPending}>
              {t('shop.order.gridGo')}
            </Button>
          </div>
          <p className="mk-hint" style={{ padding: '0 var(--mk-space-4) var(--mk-space-4)' }}>
            {t('shop.order.gridNote')}
          </p>
        </Panel>
      ) : null}
      {door !== 'grid' ? (
      <Panel>
        <div className="mk-doorpane">
          {door === 'find' ? (
            <>
              <input
                className="mk-fld"
                placeholder={t('shop.order.findPlaceholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <p className="mk-hint" style={{ marginTop: 9 }}>{t('shop.order.findHint')}</p>
            </>
          ) : null}
          {door === 'again' ? <p className="mk-hint">{t('shop.order.againHint')}</p> : null}
          {door === 'paste' ? (
            <>
              <p className="mk-hint" style={{ marginBottom: 10 }}>{t('shop.order.pasteHint')}</p>
              <textarea
                className="mk-paste"
                spellCheck={false}
                placeholder={t('shop.order.pastePlaceholder')}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
                <Button onClick={applyPaste} disabled={!pasteText.trim() || setLine.isPending}>
                  {t('shop.order.pasteGo')}
                </Button>
                <Button tone="ghost" onClick={() => setPasteText('')}>{t('shop.order.pasteClear')}</Button>
              </div>
              {pasteNote ? (
                pasteNote.missed.length ? (
                  <Alert tone="us">
                    <b>{t('shop.order.pasteAdded', { count: pasteNote.added })}</b>{' '}
                    {t('shop.order.pastePartial', {
                      added: pasteNote.added,
                      missed: pasteNote.missed.length,
                      codes: pasteNote.missed.join(', '),
                    })}
                  </Alert>
                ) : (
                  <Alert tone="done">{t('shop.order.pasteAdded', { count: pasteNote.added })}</Alert>
                )
              ) : null}
            </>
          ) : null}
        </div>
        {/* One place for every door: all three add paths share `runBulk`, so
            they share where the failure is shown. */}
        {addError ? <Alert tone="stop">{addError}</Alert> : null}
        {door === 'again' && applyingAgain ? <Spinner label={t('common.loading')} /> : null}
        {door === 'again' && !applyingAgain && lastCodes.size === 0 ? (
          <Empty title={t('shop.order.againEmptyTitle')} body={t('shop.order.againEmptyBody')} />
        ) : null}
        {showTable ? (
          <Table<ShopVariant>
            rows={rows}
            rowKey={(v) => v.code}
            dense={false}
            empty={<Empty title={t('shop.order.emptyTitle')} body={t('shop.order.emptyBody')} />}
            columns={[
              {
                header: t('shop.order.colPiece'),
                render: (v) => (
                  <>
                    <div>{v.baseCode}</div>
                    <div className="mk-dim">{v.product ?? dash}</div>
                  </>
                ),
              },
              {
                header: t('shop.order.colPurity'),
                render: (v) => <span className="mk-dim">{v.purity ?? dash}</span>,
              },
              {
                header: t('shop.order.colColour'),
                render: (v) => <span className="mk-dim">{v.metalColour ?? dash}</span>,
              },
              {
                header: t('shop.order.colPrice'), numeric: true,
                render: (v) => <Money amount={v.listPrice} currency={currency} />,
              },
              {
                header: t('shop.order.colQuantity'), numeric: true,
                render: (v) => (
                  <QtyStepper
                    value={cartByCode.get(v.code)?.quantity ?? 0}
                    disabled={!canOrder || setLine.isPending}
                    onCommit={(n) => setLine.mutate({ variantCode: v.code, quantity: n })}
                  />
                ),
              },
              {
                header: t('shop.order.colTotalPrice'), numeric: true,
                render: (v) => {
                  const line = cartByCode.get(v.code);
                  return line
                    ? <Money amount={line.lineTotal} currency={currency} />
                    : <span className="mk-dim">{dash}</span>;
                },
              },
            ]}
          />
        ) : null}
      </Panel>
      ) : null}

      <div className="mk-orderbar">
        <div className="mk-orderbar-in">
          <div>
            <div className="mk-orderbar-k">{t('shop.order.barLines')}</div>
            <div className="mk-orderbar-v">{lineCount}</div>
          </div>
          <div>
            <div className="mk-orderbar-k">{t('shop.order.barPieces')}</div>
            <div className="mk-orderbar-v">{pieces}</div>
          </div>
          <div>
            <div className="mk-orderbar-k">{t('shop.order.barTotal')}</div>
            <div className="mk-orderbar-v"><Money amount={total} currency={currency} /></div>
          </div>
          {/*
            * THE CREDIT METER IS GONE — D37 retired the credit limit and the inventory
            * budget replaced it.
            *
            * It was not merely stale, it was firing for everyone. Every account's
            * `credit_limit` is now `'0.0000'`, and a non-empty string is truthy in
            * JavaScript, so `limit` passed its own guard, the projected balance exceeded
            * zero the moment anything entered the cart, and the meter rendered full and red
            * with "over limit" against a limit that does not exist. Eleven of eleven
            * retailers on dev are in that state.
            *
            * What actually caps ordering is BG-04, the inventory budget, enforced inside the
            * order transaction behind an advisory lock. The shop shows that on its own
            * panel, and it is the server that refuses — not a meter drawn from a retired
            * column.
            */}
          <span className="mk-spacer" />
          <Button tone="ghost" disabled={lineCount === 0 || clearCart.isPending} onClick={() => clearCart.mutate()}>
            {t('shop.order.clear')}
          </Button>
          {lineCount > 0 ? (
            <Link className="mk-btn" to="/shop/order/review">{t('shop.order.review', { count: lineCount })}</Link>
          ) : (
            <span
              className="mk-btn"
              style={{
                pointerEvents: 'none', background: 'var(--mk-surface-2)',
                borderColor: 'var(--mk-rule)', color: 'var(--mk-ink-3)',
              }}
            >
              {t('shop.order.reviewEmpty')}
            </span>
          )}
        </div>
      </div>
    </>
  );
}

/** R-05 — review the cart, then submit (R-06). */
export function ShopOrderReview() {
  const { t } = useTranslation();
  const { me } = useSession();
  const navigate = useNavigate();
  const { data: cart, isLoading, error } = useCart();
  const submitOrder = useSubmitOrder();
  const removeLine = useSetCartLine();
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * BG-04 — the figures from a refused checkout. `ceiling` is the limit that refused it, which
   * since the tolerance band is NOT the available budget; a null ceiling means the API did not
   * send one, and the server's own sentence is shown instead of an amount we would be guessing.
   */
  const [shortBudget, setShortBudget] = useState<{ available: string; ceiling: string | null; currency: string } | null>(null);
  // CR-57 / D105 — the account's KYC is not approved: the order was refused, the cart is untouched.
  const [kycBlock, setKycBlock] = useState<ReturnType<typeof kycRequired>>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  /** CR-15 — a remark edited here, after the piece is already on the order. */
  const [remarkError, setRemarkError] = useState<string | null>(null);
  /** A quantity changed here rather than by going back to the catalog. */
  const [qtyError, setQtyError] = useState<string | null>(null);
  const { data: account } = useQuery({
    queryKey: ['shop', 'account'], queryFn: () => api.get<ShopAccount>('/shop/account'),
  });
  const currency = me?.kind === 'retailer' ? me.retailer.tradingCurrency : undefined;

  const lines = cart?.lines ?? [];
  /*
   * Which lines are checked — starts empty, on every mount, no exception:
   * a fresh visit, a remount from navigating away and back, a hard refresh.
   * This used to default to "every line", initialise itself from a
   * module-scoped "remembered" set to survive a remount, and re-select
   * every remaining line whenever the cart's own codes changed — three
   * separate paths that could each turn a deliberate one-box tick into
   * "everything selected" the moment anything else about the page moved.
   * `selectedLines` below is already derived by filtering `lines` against
   * this set, so a code that leaves the cart (removed, repriced away)
   * simply stops matching anything — nothing here needs to prune it, and
   * nothing here needs to reach back into a previous mount to know what
   * was ticked. Select All (`toggleAll` below) is the only thing that ever
   * selects more than what was individually clicked.
   */
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  const pieces = lines.reduce((sum, l) => sum + l.quantity, 0);
  const selectedLines = lines.filter((l) => selected.has(l.variantCode));
  const selectedTotal = selectedLines.reduce((sum, l) => addMoney(sum, l.lineTotal), '0.0000');
  const allSelected = lines.length > 0 && selectedLines.length === lines.length;
  const toggle = (code: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(lines.map((l) => l.variantCode)));

  /** Sequential on purpose, same reasoning as `ShopOrder`'s `bulkApply` —
   *  one cart, upserted one line at a time by `CartService.setLine`. */
  const removeSelected = async () => {
    setRemoveError(null);
    setRemoving(true);
    try {
      for (const code of selected) {
        await removeLine.mutateAsync({ variantCode: code, quantity: 0 });
      }
    } catch (e) {
      setRemoveError(e instanceof ApiError ? e.message : t('shop.order.removeFailed'));
    } finally {
      setRemoving(false);
    }
  };


  if (lines.length === 0) {
    return (
      <>
        <PageHeader title={t('shop.order.nothingTitle')} sub={t('shop.order.nothingBody')} />
        <Link className="mk-btn" to="/shop/order">{t('shop.order.backToOrder')}</Link>
      </>
    );
  }

  const termsItems: Array<[string, ReactNode]> = [
    [t('shop.order.orderTotal'), <Money amount={selectedTotal} currency={currency} />],
  ];
  /*
   * D75 — "Terms on this order" states the rule IN FORCE, from the one formatter.
   *
   * It read `Net {days}` off `account.terms.paymentTermsDays`, and this panel is where the
   * defect was proved: GenZ Jewels saw "Payment terms: Net 30" here while SI-GENZ-0001, issued
   * 29-Oct, fell due 07-Nov under GCC's month-end-plus-seven. The portal told the retailer a
   * term it then ignored, on the screen they confirm an order on.
   *
   * Outside the `account?.terms` test, because the due rule does not depend on the account
   * having a trading-terms row — with no row it takes the portal's rule, which is still the rule
   * this order's consignment invoices will be dated by.
   *
   * Payment terms are still shown at all because they decide when a payable falls due (FN-11).
   * "Prepayment" and the Incoterm are not: no payment is asked for before shipment under
   * consignment (SH-06) and the Incoterm went with the old shipment terms, so a zero-day term
   * reads as net 0 rather than as a demand for money up front.
   */
  if (account) {
    const inForce = dueRuleWords(account.dueRule);
    termsItems.push([t('shop.order.paymentTerms'), t(inForce.key, inForce.values)]);
  }
  if (account?.terms) {
    termsItems.push([t('shop.order.accountManager'), account.terms.accountManager ?? dash]);
  }
  termsItems.push([t('shop.order.whatNext'), t('shop.order.whatNextBody')]);

  return (
    <>
      <PageHeader title={t('shop.order.reviewTitle')} />
      {/* Same retired gate, same always-true condition — see the order bar. BG-04 refuses an
          order past its budget at the server, with a message naming the real figure. */}
      {removeError ? <Alert tone="stop">{removeError}</Alert> : null}
      {remarkError ? <Alert tone="stop">{remarkError}</Alert> : null}
      {qtyError ? <Alert tone="stop">{qtyError}</Alert> : null}
      {/* Said before the table, not discovered at a disabled button: nothing is
          ticked on arrival, on purpose, so part of a cart can be ordered. */}
      {selectedLines.length === 0 ? <Note>{t('shop.order.pickLinesHint')}</Note> : null}
      <Panel
        title={t('shop.order.linesPieces', { lines: lines.length, pieces })}
        actions={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--mk-space-3)' }}>
            <span className="mk-dim">
              {t('shop.order.selectedCount', { count: selectedLines.length })}
            </span>
            <button
              type="button"
              className="mk-btn mk-btn-g mk-btn-s"
              disabled={selectedLines.length === 0 || removing}
              onClick={() => void removeSelected()}
            >
              {removing ? t('common.loading') : t('shop.order.removeSelected')}
            </button>
          </div>
        )}
      >
        <Table
          rows={lines}
          rowKey={(l) => l.variantCode}
          empty={<Empty title={t('shop.order.emptyTitle')} />}
          columns={[
            {
              // Select all / clear all, above the row boxes it acts on — the
              // same header checkbox as the admin product list, not a text
              // link off in the panel header. Partial reads as indeterminate,
              // which has no prop, only a DOM property.
              header: (
                <input
                  type="checkbox"
                  className="mk-rowcheck"
                  checked={allSelected}
                  ref={(el: HTMLInputElement | null) => {
                    if (el) el.indeterminate = selectedLines.length > 0 && !allSelected;
                  }}
                  onChange={toggleAll}
                  aria-label={allSelected ? t('shop.order.selectNone') : t('shop.order.selectAll')}
                  title={allSelected ? t('shop.order.selectNone') : t('shop.order.selectAll')}
                />
              ),
              width: '1%',
              center: true,
              render: (l) => (
                <input
                  type="checkbox"
                  className="mk-rowcheck"
                  checked={selected.has(l.variantCode)}
                  onChange={() => toggle(l.variantCode)}
                  aria-label={t('shop.order.selectLine', { name: l.name ?? l.variantCode })}
                />
              ),
            },
            {
              header: t('shop.order.colPiece'),
              center: true,
              render: (l) => (
                <div className="mk-piece">
                  <ThumbPreview url={l.imageUrl} label={l.name ?? l.variantCode} />
                  <div>
                    <div className="mk-piece-name">{l.name ?? dash}</div>
                    {/* Two lines of one design differ only in metal or stone,
                        so say which in words; the code alone does not. */}
                    {l.spec ? <div className="mk-piece-spec">{l.spec}</div> : null}
                    <div className="mk-dim mk-piece-code">{l.baseCode ?? l.variantCode}</div>
                  </div>
                </div>
              ),
            },
            {
              /*
               * CR-15 — editable HERE, not merely displayed.
               *
               * A remark is the one part of a line a buyer thinks of late:
               * the engraving, the packing, the note for the bench. Having to
               * go back to the catalog screen, find the piece among 180 and
               * re-add it just to say "gift box" is the kind of detour that
               * ends with the remark going in an email instead, where nothing
               * carries it onto the order.
               *
               * Saved on blur rather than per keystroke — every cart write
               * re-reads the whole cart, so saving as you type would be a
               * round trip per character.
               */
              header: t('shop.order.colRemark'),
              center: true,
              width: '39%',
              render: (l) => (
                <input
                  className="mk-line-remark"
                  defaultValue={l.remark ?? ''}
                  maxLength={1000}
                  placeholder={t('shop.order.remarkPlaceholder')}
                  aria-label={t('shop.order.remarkFor', { code: l.variantCode })}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    if (next === (l.remark ?? '')) return;
                    setRemarkError(null);
                    void removeLine.mutateAsync({
                      variantCode: l.variantCode,
                      quantity: l.quantity,
                      ...(l.size ? { size: l.size } : {}),
                      ...(next ? { remark: next } : {}),
                    }).catch((err: unknown) => {
                      // Said out loud, and the box keeps what was typed — a
                      // remark silently not saved is worse than one that failed.
                      setRemarkError(err instanceof Error ? err.message : t('shop.order.addFailed'));
                    });
                  }}
                />
              ),
            },
            // Every column is centred, header and value alike, so no heading
            // sits at a different edge from its neighbours. Fixed widths keep
            // the gaps between columns even rather than letting the piece and
            // remark columns sprawl.
            {
              /*
               * Editable, saved on blur or Enter — the same write the remark
               * makes, carrying size and remark so neither is lost. Below 1 or
               * not a number puts the old value back: removing a line is
               * "Remove selected", never a quantity typed as 0 by accident.
               * Keyed on the quantity so a refetched cart resets the box.
               */
              header: t('shop.order.colQuantity'),
              center: true,
              width: '8%',
              render: (l) => (
                <input
                  key={`${l.variantCode}:${l.quantity}`}
                  className="mk-qty-in"
                  inputMode="numeric"
                  defaultValue={String(l.quantity)}
                  aria-label={t('shop.order.qtyFor', { code: l.variantCode })}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  onBlur={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    // A COUNT, not money — an integer is correct (invariant 1).
                    const next = digits === '' ? 0 : Math.min(100_000, Number(digits));
                    if (next < 1) { e.target.value = String(l.quantity); return; }
                    if (next === l.quantity) { e.target.value = String(next); return; }
                    setQtyError(null);
                    void removeLine.mutateAsync({
                      variantCode: l.variantCode,
                      quantity: next,
                      ...(l.size ? { size: l.size } : {}),
                      ...(l.remark ? { remark: l.remark } : {}),
                    }).catch((err: unknown) => {
                      e.target.value = String(l.quantity);
                      setQtyError(err instanceof Error ? err.message : t('shop.order.qtyFailed'));
                    });
                  }}
                />
              ),
            },
            {
              header: t('shop.order.gridColSize'),
              center: true,
              width: '10%',
              render: (l) => l.size ?? dash,
            },
            {
              header: t('shop.order.colUnit'), center: true, width: '9%',
              render: (l) => <Money amount={l.listPrice} currency={currency} />,
            },
            {
              header: t('shop.order.colTotalPrice'), center: true, width: '9%',
              render: (l) => <Money amount={l.lineTotal} currency={currency} />,
            },
          ]}
        />
      </Panel>
      <Panel title={t('shop.order.termsTitle')} pad>
        <Rail items={termsItems} />
        <Note style={{ marginTop: 'var(--mk-space-4)' }}>{t('shop.order.pricesNote')}</Note>
      </Panel>
      {/*
        * BG-04 — the refusal names the figure that actually refused it: the ordering CEILING,
        * not the available budget. With a tolerance band those are different amounts, and
        * "you have 15,000 available" on an account that would have taken 15,750 sends the
        * retailer back to the cart to retry a total the server has already decided against.
        *
        * The tolerance itself is not shown. Publishing "you may go 5% over" turns an overdraft
        * into budget, and the retailer needs the limit, not its construction.
        *
        * Rendered through <Money>, never string interpolation: a decimal string formatted by
        * the component that knows the currency (NF-04, UI-04).
        */}
      {/* CR-57 / D105 — says which of three things is true, and links to R-18; see KycRefusal. */}
      {kycBlock ? <KycRefusal kyc={kycBlock} /> : null}
      {shortBudget?.ceiling ? (
        <Alert tone="stop">
          <Trans i18nKey="shop.order.overBudget" components={{ m: <Money amount={shortBudget.ceiling} currency={shortBudget.currency} /> }} />
        </Alert>
      ) : submitError ? <Alert tone="stop">{submitError}</Alert> : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--mk-space-4)' }}>
        <Button
          disabled={submitOrder.isPending || selectedLines.length === 0}
          onClick={() => {
            setSubmitError(null);
            setShortBudget(null);
            setKycBlock(null);
            submitOrder.mutate(
              selectedLines.length < lines.length ? { variantCodes: selectedLines.map((l) => l.variantCode) } : undefined,
              {
                onSuccess: (order) => void navigate(`/shop/orders/${order.id}`),
                onError: (e) => {
                  // KYC first: it is checked before the budget on the server too, and a retailer who
                  // is not approved must be told that rather than "that did not work".
                  const kyc = kycRequired(e);
                  if (kyc) { setKycBlock(kyc); return; }
                  const short = budgetExceeded(e);
                  // Only when the payload carries the ceiling. An older API sends none, and the
                  // server's own sentence is better than an alert with a blank amount in it.
                  if (short?.ceiling) { setShortBudget(short); return; }
                  setSubmitError(e instanceof ApiError ? e.message : t('shop.order.submitFailed'));
                },
              },
            );
          }}
        >
          {submitOrder.isPending
            ? t('common.loading')
            : selectedLines.length === 0
              ? t('shop.order.submitPick')
              : selectedLines.length < lines.length
              ? t('shop.order.submitCount', { count: selectedLines.length })
              : t('shop.order.submit')}
        </Button>
        <Link className="mk-btn mk-btn-g" to="/shop/catalog">{t('shop.order.keepAdding')}</Link>
      </div>
    </>
  );
}

// -------------------------------------------------------------------- R-06/R-07

const ORDER_STATUS_TONE: Record<string, Tone> = {
  SUBMITTED: 'them', UNDER_REVIEW: 'them', AWAITING_ACCEPTANCE: 'us', APPROVED: 'done',
  AWAITING_ADVANCE: 'us', IN_MANUFACTURING: 'run', READY: 'run', SHIPPED: 'run',
  DELIVERED: 'done', CANCELLED: 'lapse', REJECTED: 'stop', EXPIRED: 'lapse',
};
const PROOF_STATUS_TONE: Record<PaymentProofStatus, Tone> = { PENDING: 'us', VERIFIED: 'done', REJECTED: 'stop' };

/** Lifecycle order, which is the order the status filter lists them in. */
const ORDER_STATUS_RANK = Object.keys(ORDER_STATUS_TONE);
const statusRank = (s: string) => {
  const i = ORDER_STATUS_RANK.indexOf(s);
  return i === -1 ? ORDER_STATUS_RANK.length : i;
};
/** AWAITING_ACCEPTANCE -> "Awaiting acceptance". */
const statusLabel = (s: string) => {
  const words = s.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
/** A date-only string ("2026-10-01") is a calendar day, not a UTC instant — parsed as one it would show a day early west of Greenwich. */
const calendarDay = (s: string) => new Date(s.length === 10 ? `${s}T00:00:00` : s).toLocaleDateString();

/** R-06 — every order this account has placed. */
export function ShopOrders() {
  const { t } = useTranslation();
  const { me } = useSession();
  const { data, isLoading, error } = useMyOrders();
  const currency = me?.kind === 'retailer' ? me.retailer.tradingCurrency : undefined;
  /**
   * Search, status, payment status and the date range, all held in the address (`?q=&status=&payment=&from=&to=`)
   * rather than in component state, so opening an order or a consignment invoice and coming back lands on the same
   * filtered list, and a filtered view can be bookmarked. They apply together over the whole list.
   */
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const filters = filtersFromParams(params);
  const status = filters.status;
  const change = (patch: Partial<OrderFilters>) => setParams(paramsFromFilters({ ...filters, ...patch }), { replace: true });
  const filtering = isFiltering(filters);
  const here = `${location.pathname}${location.search}`;

  const orders = useMemo(() => data ?? [], [data]);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of orders) m.set(o.status, (m.get(o.status) ?? 0) + 1);
    return m;
  }, [orders]);
  // Only statuses this account actually has orders in — an option that can only
  // ever answer "nothing" is noise. A selection whose orders have since moved on
  // stays listed, so the select never shows a value it has no option for.
  const statuses = useMemo(() => {
    const present = [...counts.keys()];
    if (status && !counts.has(status)) present.push(status);
    return present.sort((a, b) => statusRank(a) - statusRank(b));
  }, [counts, status]);
  const shown = filterOrders(orders, filters);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  return (
    <>
      {/* No page title: the active "Orders" tab in the top navigation already names this page. */}
      {orders.length > 0 ? (
        <div className="mk-toolbar">
          <input
            className="mk-fld mk-toolbar-search"
            value={filters.q}
            placeholder={t('shop.orders.searchPlaceholder')}
            aria-label={t('shop.orders.searchLabel')}
            onChange={(e) => change({ q: e.target.value })}
          />
          <select
            value={status}
            aria-label={t('shop.orders.statusFilterLabel')}
            onChange={(e) => change({ status: e.target.value })}
          >
            <option value="">{t('shop.orders.allStatuses')}</option>
            {statuses.map((s) => (
              <option key={s} value={s}>{`${statusLabel(s)} (${counts.get(s) ?? 0})`}</option>
            ))}
          </select>
          <label className="mk-toolbar-check">
            {t('shop.orders.fromLabel')}
            <input
              type="date" className="mk-fld" value={filters.from} max={filters.to || undefined}
              aria-label={t('shop.orders.dateFromLabel')} onChange={(e) => change({ from: e.target.value })}
            />
          </label>
          <label className="mk-toolbar-check">
            {t('shop.orders.toLabel')}
            <input
              type="date" className="mk-fld" value={filters.to} min={filters.from || undefined}
              aria-label={t('shop.orders.dateToLabel')} onChange={(e) => change({ to: e.target.value })}
            />
          </label>
          {filtering ? (
            <>
              <span className="mk-dim">{t('shop.orders.showingCount', { shown: shown.length, total: orders.length })}</span>
              <button type="button" className="mk-linkish" onClick={() => setParams(paramsFromFilters(NO_FILTERS), { replace: true })}>
                {t('shop.orders.clearFilters')}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      <Panel>
        <Table<RetailerOrderSummary>
          rows={shown}
          rowKey={(o) => o.id}
          // Auto layout with wrapping cells (`.mk-orders-wide`): eleven columns
          // fit a normal desktop width without sideways scrolling, and a narrow
          // screen scrolls the table rather than clipping a column.
          className="mk-orders-wide"
          empty={orders.length === 0
            ? <Empty title={t('shop.orders.emptyTitle')} body={t('shop.orders.emptyBody')} />
            : <Empty title={t('shop.orders.noMatchTitle')} body={t('shop.orders.noMatchBody')} />}
          columns={[
            {
              header: t('shop.orders.colOrderNo'),
              render: (o) => (
                <>
                  <Link to={`/shop/orders/${o.id}`} state={{ backTo: here }}>{o.reference}</Link>
                  <div className="mk-dim">{new Date(o.submittedAt).toLocaleDateString()}</div>
                </>
              ),
            },
            { header: t('shop.orders.colItemName'), render: (o) => <ItemNames names={o.items} /> },
            {
              header: t('shop.orders.colItems'),
              numeric: true,
              render: (o) => o.pieces,
            },
            {
              header: t('shop.orders.colStatus'),
              render: (o) => <Chip tone={ORDER_STATUS_TONE[o.status] ?? 'lapse'}>{o.status.replace(/_/g, ' ')}</Chip>,
            },
            {
              header: t('shop.orders.colPrice'),
              numeric: true,
              render: (o) => <Money amount={o.agreedTotal} currency={currency ?? o.currency} />,
            },
            {
              header: t('shop.orders.colContact'),
              render: (o) => o.orderContact ?? dash,
            },
            {
              // The newest thing that happened to the order, not when it was placed.
              header: t('shop.orders.colUpdated'),
              render: (o) => (o.lastUpdatedAt
                ? new Date(o.lastUpdatedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
                : dash),
            },
            {
              header: t('shop.orders.colShipment'),
              render: (o) => (o.shipments.length === 0 ? dash : o.shipments.map((s) => {
                const line = shipmentLine(s);
                return (
                  <div key={s.id}>
                    {line.title}
                    {line.detail ? <div className="mk-dim">{line.detail}</div> : null}
                    <ShipmentStatus shipment={s} />
                  </div>
                );
              })),
            },
            {
              // Delivered date when it has happened; the expected date, labelled,
              // while it has not — a date never invented.
              header: t('shop.orders.colDelivered'),
              render: (o) => (o.deliveredAt
                ? new Date(o.deliveredAt).toLocaleDateString()
                : o.expectedDeliveryDate
                  ? <span className="mk-dim">{t('shop.orders.expectedOn', { date: calendarDay(o.expectedDeliveryDate) })}</span>
                  : dash),
            },
            {
              header: t('shop.orders.colActions'),
              render: (o) => <OrderActions order={o} />,
            },
          ]}
        />
      </Panel>
    </>
  );
}

/** R-07 — one order: its lines, accept/decline while awaiting acceptance,
 *  queries, and submitting a payment proof against it (PY-07/PY-08). */
export function ShopOrderDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { me } = useSession();
  const backTo = backToOrders(useLocation().state);
  const { data: o, isLoading, error } = useMyOrder(id);
  const { data: payments } = useOrderPayments(id);
  const invoices = useShopOrderConsignmentInvoices(id);
  const accept = useAcceptOrder();
  const decline = useDeclineOrder();
  const requestRevision = useRequestRevision();
  const sendMessage = useSendMyOrderMessage(id ?? '');
  const submitProof = useSubmitPaymentProof(id ?? '');
  const currency = me?.kind === 'retailer' ? me.retailer.tradingCurrency : undefined;

  const [failed, setFailed] = useState<string | null>(null);
  /** Which answer to the offer is being written, if any — feedback or a decline. */
  const [answering, setAnswering] = useState<'feedback' | 'decline' | null>(null);
  const [feedback, setFeedback] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [payForm, setPayForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [bankReference, setBankReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // null = the retailer has not touched the selector: with exactly one consignment invoice it is
  // preselected.
  const [payInvoiceChoice, setPayInvoice] = useState<string | null>(null);
  const [paidNote, setPaidNote] = useState(false);
  const { data: milestones } = useShopOrderMilestones(id);
  // What the typed amount means before it is sent (milestone-scheduled orders only).
  const notice = milestones?.scheduled
    ? noticeFor(amount, { amountDueNow: milestones.amountDueNow, payableNow: milestones.payableNow })
    : ({ kind: 'empty' } as const);

  const soleInvoice = (invoices.data ?? []).length === 1 ? invoices.data![0]!.id : '';
  const payInvoice = payInvoiceChoice ?? soleInvoice;

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!o) return null;

  async function run(fn: () => Promise<unknown>) {
    setFailed(null);
    try { await fn(); } catch (e) { setFailed(e instanceof ApiError ? e.message : t('shop.orders.actionFailed')); }
  }

  const canDecide = o.availableActions.includes('accept');
  // Sent back and not yet re-offered: say so, rather than leave the retailer
  // wondering where the Accept button went.
  const awaitingRevision = o.status === 'UNDER_REVIEW'
    && o.queries.some((q) => q.subject === REVISION_FEEDBACK_SUBJECT && q.status === 'OPEN');

  return (
    <>
      {/* A detail screen reached from a list needs a way back to it. Browser back
          works, but a person who arrived from a link, a bookmark or a pasted URL
          has no history to go back through. */}
      <div className="mk-backlink">
        <Link to={backTo}>&larr; {t('shop.backToOrders')}</Link>
      </div>
      <EditOrder order={o} currency={currency}>
        {({ button, notes, bar, quantityCell, sizeCell }) => (
          <>
            <PageHeader
              title={o.reference}
              sub={t('shop.orders.submittedOn', { date: new Date(o.submittedAt).toLocaleDateString() })}
              actions={(
                <>
                  <Chip tone={ORDER_STATUS_TONE[o.status] ?? 'lapse'}>{o.status.replace(/_/g, ' ')}</Chip>
                  {button}
                </>
              )}
            />
            {notes}
            {failed ? <Alert tone="stop">{failed}</Alert> : null}
            {bar}

      <Panel title={t('shop.orders.linesCount', { count: o.lines.length })}>
        <Table
          rows={o.lines}
          rowKey={(l) => String(l.lineNumber)}
          columns={[
            {
              header: t('shop.orders.editColItem'),
              render: (l) => (
                <div className="mk-piece">
                  <span className="mk-thumb">
                    {l.imageUrl ? <img src={l.imageUrl} alt="" /> : null}
                  </span>
                  <div>
                    <div className="mk-piece-name">{l.description ?? dash}</div>
                    {l.remark ? <div className="mk-dim mk-piece-remark">{l.remark}</div> : null}
                  </div>
                </div>
              ),
            },
            { header: t('shop.orders.editColVariant'), render: (l) => <code>{l.baseCode ?? l.variantCode}</code> },
            { header: t('shop.order.gridColSize'), render: (l) => sizeCell(l) },
            { header: t('shop.order.colQuantity'), numeric: true, render: (l) => quantityCell(l) },
            { header: t('shop.orders.colPrice'), numeric: true, render: (l) => <Money amount={l.lineTotal} currency={currency} /> },
          ]}
        />
      </Panel>
          </>
        )}
      </EditOrder>

      <ChangeRequestsPanel orderId={o.id} />

      {o.closureReason ? <Note>{t('shop.orders.declinedReason', { reason: o.closureReason })}</Note> : null}

      {awaitingRevision ? <Alert tone="them">{t('shop.orders.revisionPending')}</Alert> : null}

      {/* NEGOTIATION REMNANT — left in place deliberately, not overlooked.
          This accept / feedback / decline panel is the retailer answering
          negotiated figures (Appendix B: OR-04, OR-13), retired at v1.6. It is
          gated on the SERVER's `availableActions.includes('accept')`, and with
          `sendForAcceptance` no longer offered on A-15 no order can enter that
          state, so the panel is already unreachable rather than merely unwanted.
          Ripping out the state, the mutation and the feedback thread would be a
          larger change than removing a label, and it belongs with retiring the
          transition server-side. Tracked in docs/plans/screen-field-audit. */}
      {canDecide ? (
        <Panel title={t('shop.orders.decisionTitle')} pad>
          <Note>{t('shop.orders.decisionBody')}</Note>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <Button disabled={accept.isPending || !!answering} onClick={() => void run(() => accept.mutateAsync(o.id))}>
              {t('shop.orders.accept')}
            </Button>
            <Button tone="ghost" disabled={!!answering} onClick={() => setAnswering('feedback')}>
              {t('shop.orders.sendFeedback')}
            </Button>
            <Button tone="danger" disabled={!!answering} onClick={() => setAnswering('decline')}>
              {t('shop.orders.decline')}
            </Button>
          </div>
          {answering === 'feedback' ? (
            <div style={{ marginTop: 12 }}>
              <Field label={t('shop.orders.feedbackLabel')}>
                <textarea
                  className="mk-fld"
                  rows={4}
                  value={feedback}
                  maxLength={4000}
                  onChange={(e) => setFeedback(e.target.value)}
                />
              </Field>
              <Note>{t('shop.orders.feedbackHint')}</Note>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button
                  disabled={feedback.trim().length < 3 || requestRevision.isPending}
                  onClick={() => void run(async () => {
                    await requestRevision.mutateAsync({ id: o.id, feedback: feedback.trim() });
                    setAnswering(null); setFeedback('');
                  })}
                >
                  {t('shop.orders.sendFeedbackConfirm')}
                </Button>
                <Button tone="ghost" onClick={() => { setAnswering(null); setFeedback(''); }}>{t('common.cancel')}</Button>
              </div>
            </div>
          ) : null}
          {answering === 'decline' ? (
            <div style={{ marginTop: 12 }}>
              <Field label={t('shop.orders.declineReasonLabel')}>
                <input
                  className="mk-fld"
                  value={declineReason}
                  maxLength={1000}
                  onChange={(e) => setDeclineReason(e.target.value)}
                />
              </Field>
              <Note>{t('shop.orders.declineHint')}</Note>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button
                  tone="danger"
                  disabled={decline.isPending}
                  onClick={() => void run(async () => {
                    await decline.mutateAsync({ id: o.id, reason: declineReason });
                    setAnswering(null); setDeclineReason('');
                  })}
                >
                  {t('shop.orders.declineConfirm')}
                </Button>
                <Button tone="ghost" onClick={() => { setAnswering(null); setDeclineReason(''); }}>{t('common.cancel')}</Button>
              </div>
            </div>
          ) : null}
        </Panel>
      ) : null}

      <Panel title={t('shop.consignmentInvoice.panelTitle')}>
        {invoices.isLoading ? (
          <div role="status"><Spinner /><span className="mk-dim">{t('shop.consignmentInvoice.loading')}</span></div>
        ) : invoices.error ? (
          <Alert tone="stop">{t('shop.consignmentInvoice.loadFailed')}</Alert>
        ) : (
          <Table
            rows={invoices.data ?? []}
            rowKey={(p) => p.id}
            empty={<Empty title={t('shop.consignmentInvoice.none')} />}
            columns={[
              { header: t('shop.consignmentInvoice.colNumber'), render: (p) => <Link to={`/shop/consignment-invoices/${p.id}`} state={invoiceLinkState(`${ORDERS_HOME}/${id}`, backTo)}>{p.reference}</Link> },
              { header: t('shop.consignmentInvoice.colCreated'), render: (p) => new Date(p.createdAt).toLocaleDateString() },
              /* The consigned value only: the goods are not a debt (SH-06) and no courier charge is
                 payable either (04-Nov-2026). Money owed is on the purchase invoice (R-22). */
              { header: t('despatch.colConsigned'), numeric: true, render: (p) => <Money amount={p.consignedValue} currency={p.currency} /> },
              {
                header: t('shop.consignmentInvoice.colShipment'),
                render: (p) => (p.shipment
                  ? <Chip tone={SHIPMENT_TONE[p.shipment.status]}>{shipmentStateLabel(p.shipment.status)}</Chip>
                  : <span className="mk-dim">{t('shop.consignmentInvoice.noShipmentShort')}</span>),
              },
            ]}
          />
        )}
      </Panel>

      {/* 30/30/30/10 — the same figures the admin sees; nothing for an order approved before it. */}
      <RetailerMilestones orderId={id} />
      {paidNote ? <Alert tone="done">{t('order.milestones.submitted')}</Alert> : null}

      <Panel
        title={t('shop.orders.paymentsTitle')}
        actions={!payForm ? <Button size="sm" tone="ghost" onClick={() => setPayForm(true)}>{t('shop.orders.addPayment')}</Button> : null}
      >
        <Table<PaymentProof>
          rows={payments ?? []}
          rowKey={(p) => p.id}
          empty={<Empty title={t('shop.orders.noPayments')} />}
          columns={[
            { header: t('shop.orders.colSubmitted'), render: (p) => new Date(p.submittedAt).toLocaleDateString() },
            {
              header: t('shop.orders.amount'), numeric: true,
              render: (p) => (
                <>
                  <Money amount={p.amount} currency={p.currency} />
                  {p.purpose === 'COURIER' ? <div className="mk-dim">{t('shop.consignmentInvoice.courierCharged')}</div> : null}
                </>
              ),
            },
            { header: t('shop.orders.colStatus'), render: (p) => <Chip tone={PROOF_STATUS_TONE[p.status]}>{p.status}</Chip> },
            {
              header: '',
              render: (p) => (p.status === 'REJECTED'
                ? <span className="mk-dim">{p.rejectionReason}</span>
                : (p.hasFile ? <a href={`/api/shop/payments/${p.id}/file`} target="_blank" rel="noreferrer">{t('shop.orders.viewFile')}</a> : null)),
            },
          ]}
        />
        {payForm ? (
          <div style={{ padding: 16, borderTop: '1px solid var(--mk-rule)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label={t('shop.orders.amount')}>
              <input className="mk-fld" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </Field>
            <Field label={t('shop.orders.paidOn')}>
              <input className="mk-fld" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            </Field>
            <Field label={t('shop.orders.bankReference')}>
              <input className="mk-fld" value={bankReference} onChange={(e) => setBankReference(e.target.value)} />
            </Field>
            {/* Which CONSIGNMENT INVOICE this payment is attributed to (30/30/30/10). Optional:
                leaving it on "whole order" is valid, and Admin may assign it later. Only for an
                order on the schedule. This is an order payment, not a settlement of the despatch:
                the consigned goods are never payable (SH-06). */}
            {milestones?.scheduled && (invoices.data ?? []).length > 0 ? (
              <Field label={t('order.milestones.forInvoice')}>
                <select className="mk-fld" value={payInvoice} onChange={(e) => setPayInvoice(e.target.value)}>
                  <option value="">{t('order.milestones.orderLevel')}</option>
                  {(invoices.data ?? []).map((i) => <option key={i.id} value={i.id}>{i.reference}</option>)}
                </select>
              </Field>
            ) : null}
            <Field label={t('shop.orders.file')}>
              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </Field>
            {/* What the typed amount means, before it is sent: more than can be paid now is
                refused here (and by the API), less than due is allowed with a plain warning. */}
            {milestones?.scheduled && notice.kind !== 'empty' && notice.kind !== 'ok' ? (
              <div style={{ flexBasis: '100%' }}>
                <Alert tone={notice.kind === 'over' || notice.kind === 'invalid' ? 'stop' : 'us'}>
                  {notice.kind === 'over'
                    ? <>{t('order.milestones.amountOver', { amount: `${o.currency} ${notice.mostPayable}` })} {milestones.holdReason ?? ''}</>
                    : notice.kind === 'under'
                      ? t('order.milestones.amountUnder', { due: `${o.currency} ${notice.due}`, owed: `${o.currency} ${notice.stillOwed}` })
                      : t('order.milestones.amountInvalid')}
                </Alert>
              </div>
            ) : null}
            {milestones?.scheduled && notice.kind === 'empty' ? (
              <div style={{ flexBasis: '100%' }} className="mk-dim">
                {milestones.payableNow === '0.0000'
                  ? (milestones.holdReason ?? t('order.milestones.nothingPayable'))
                  : `${t('order.milestones.dueNow', { amount: `${o.currency} ${milestones.amountDueNow}` })} · ${t('order.milestones.payableNow', { amount: `${o.currency} ${milestones.payableNow}` })}`}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <Button
                disabled={!amount.trim() || submitProof.isPending
                  || (milestones?.scheduled === true && (notice.kind === 'over' || notice.kind === 'invalid'))}
                onClick={() => void run(async () => {
                  const proof = await submitProof.mutateAsync({
                    amount, currency: currency ?? o.currency, paidOn,
                    bankReference: bankReference.trim() || undefined,
                    // `consignmentInvoiceId` is the FROZEN wire field name for the consignment
                    // invoice's id — it is what the API answers with and what the browser sends
                    // (`zMilestoneProof`). D97 Wave 3 renames the declaration and all ~110 call
                    // sites in one commit; renaming it here alone would not compile.
                    consignmentInvoiceId: milestones?.scheduled && payInvoice ? payInvoice : undefined,
                  });
                  if (file) await uploadPaymentProofFile(proof.id, file);
                  setPayForm(false); setAmount(''); setBankReference(''); setFile(null); setPayInvoice(null);
                  setPaidNote(true); setPaidNote(true);
                })}
              >
                {t('shop.orders.submitPayment')}
              </Button>
              <Button tone="ghost" onClick={() => setPayForm(false)}>{t('common.cancel')}</Button>
            </div>
          </div>
        ) : null}
      </Panel>

      {/* OR-14 — the order's conversation with Miraki, for its whole working
          life; earlier questions and revision feedback read back into it. */}
      <OrderChat
        messages={o.conversation}
        mySide="retailer"
        canSend={o.canMessage && !!me?.permissions.includes('shop.query')}
        onSend={(text) => sendMessage.mutateAsync(text)}
        labels={{
          title: t('shop.orders.chatTitle'),
          empty: t('shop.orders.chatEmpty'),
          placeholder: t('shop.orders.chatPlaceholder'),
          send: t('shop.orders.chatSend'),
          sending: t('shop.orders.chatSending'),
          closed: o.canMessage ? t('shop.orders.chatNoPermission') : t('shop.orders.chatClosed'),
          failed: t('shop.orders.chatFailed'),
        }}
      />
    </>
  );
}
